import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import type { ApiResponse } from '@/types'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'

// Auth types
export interface UserAuthInfo {
  user_id: string
  tenant_id: string
  tenant_name: string
  full_name: string
  user_login_id: string
  user_email: string
  role_id: string
  role_name: string
  is_super_admin: boolean
}

export type SubscriptionTier = 'FREE_TRIAL' | 'PREMIUM'

export interface TenantInfo {
  tenant_id: string
  tenant_name: string
  role_id: string
  role_name: string
  is_primary: boolean
  subscription_tier: SubscriptionTier
  tier_start_date: string | null
  trial_end_date: string | null
}

export interface LoginRequest {
  user_login_id: string
  password: string
}

export interface LoginResponse {
  token: string
  refresh_token: string
  expires_at: string
  user: UserAuthInfo
  tenants: TenantInfo[]
}

export interface AuthContextType {
  user: UserAuthInfo | null
  token: string | null
  tenants: TenantInfo[]
  currentTenant: TenantInfo | null
  isAuthenticated: boolean
  isLoading: boolean
  isSuperAdmin: boolean
  login: (data: LoginRequest) => Promise<void>
  verifyOtp: (sessionToken: string, otpCode: string) => Promise<void>
  loginWithToken: (token: string) => Promise<void>
  logout: () => void
  switchTenant: (tenantId: string) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Storage keys
const TOKEN_KEY = 'auth_token'
const REFRESH_TOKEN_KEY = 'auth_refresh_token'
const TOKEN_EXPIRES_KEY = 'auth_token_expires'
const USER_KEY = 'auth_user'
const TENANTS_KEY = 'auth_tenants'
const TENANT_KEY = 'tenant_id'
const USER_ID_KEY = 'user_id'

// Refresh token 1 minute before expiry
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserAuthInfo | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [tokenExpiresAt, setTokenExpiresAt] = useState<Date | null>(null)
  const [tenants, setTenants] = useState<TenantInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Compute current tenant from user's tenant_id
  const currentTenant = tenants.find(t => t.tenant_id === user?.tenant_id) || null

  // Helper to store auth data
  const storeAuthData = useCallback((loginData: LoginResponse) => {
    localStorage.setItem(TOKEN_KEY, loginData.token)
    localStorage.setItem(REFRESH_TOKEN_KEY, loginData.refresh_token)
    localStorage.setItem(TOKEN_EXPIRES_KEY, loginData.expires_at)
    localStorage.setItem(USER_KEY, JSON.stringify(loginData.user))
    localStorage.setItem(TENANTS_KEY, JSON.stringify(loginData.tenants))
    localStorage.setItem(TENANT_KEY, loginData.user.tenant_id)
    localStorage.setItem(USER_ID_KEY, loginData.user.user_id)

    setToken(loginData.token)
    setRefreshToken(loginData.refresh_token)
    setTokenExpiresAt(new Date(loginData.expires_at))
    setUser(loginData.user)
    setTenants(loginData.tenants)

    // Set Sentry user context for error tracking
    setSentryUser({
      id: loginData.user.user_id,
      email: loginData.user.user_email,
      tenantId: loginData.user.tenant_id,
    })
  }, [])

  // Helper to clear auth data
  const clearAuthData = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(TOKEN_EXPIRES_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(TENANTS_KEY)
    localStorage.removeItem(TENANT_KEY)
    localStorage.removeItem(USER_ID_KEY)
    setToken(null)
    setRefreshToken(null)
    setTokenExpiresAt(null)
    setUser(null)
    setTenants([])

    // Clear Sentry user context on logout
    clearSentryUser()
  }, [])

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    const storedExpiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY)
    const storedUser = localStorage.getItem(USER_KEY)
    const storedTenants = localStorage.getItem(TENANTS_KEY)

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        const parsedTenants = storedTenants ? JSON.parse(storedTenants) : []
        setToken(storedToken)
        setRefreshToken(storedRefreshToken)
        setTokenExpiresAt(storedExpiresAt ? new Date(storedExpiresAt) : null)
        setUser(parsedUser)
        setTenants(parsedTenants)

        // Restore Sentry user context from stored user
        setSentryUser({
          id: parsedUser.user_id,
          email: parsedUser.user_email,
          tenantId: parsedUser.tenant_id,
        })
      } catch {
        // Invalid stored data, clear it
        clearAuthData()
      }
    }
    setIsLoading(false)
  }, [clearAuthData])

  // Login initiates OTP flow - this is kept for backward compatibility
  // but the actual login completion happens in verifyOtp
  const login = useCallback(async (data: LoginRequest) => {
    // This now just initiates the OTP flow
    // The response will contain session_token and requires_otp
    // The actual login completion happens in verifyOtp
    await axios.post('/api/auth/login', data)
    // Note: The LoginPage handles the OTP flow directly
  }, [])

  // Verify OTP and complete login
  const verifyOtp = useCallback(async (sessionToken: string, otpCode: string) => {
    const response = await axios.post<ApiResponse<LoginResponse>>('/api/auth/verify-otp', {
      session_token: sessionToken,
      otp_code: otpCode,
    })

    // Extract data from the API response wrapper
    const loginData = (response.data.data || response.data) as LoginResponse
    storeAuthData(loginData)
  }, [storeAuthData])

  // Refresh access token using refresh token
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!storedRefreshToken) {
      return false
    }

    try {
      const response = await axios.post<ApiResponse<LoginResponse>>('/api/auth/refresh', {
        refresh_token: storedRefreshToken,
      })

      const loginData = (response.data.data || response.data) as LoginResponse
      storeAuthData(loginData)
      return true
    } catch {
      // Refresh failed, clear auth data
      clearAuthData()
      return false
    }
  }, [storeAuthData, clearAuthData])

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!tokenExpiresAt || !refreshToken) {
      return
    }

    const now = new Date()
    const timeUntilExpiry = tokenExpiresAt.getTime() - now.getTime()
    const timeUntilRefresh = timeUntilExpiry - TOKEN_REFRESH_BUFFER_MS

    if (timeUntilRefresh <= 0) {
      // Token is about to expire or already expired, refresh now
      refreshAccessToken()
      return
    }

    // Schedule refresh
    const timeoutId = setTimeout(() => {
      refreshAccessToken()
    }, timeUntilRefresh)

    return () => clearTimeout(timeoutId)
  }, [tokenExpiresAt, refreshToken, refreshAccessToken])

  const logout = useCallback(async () => {
    // Call backend logout to revoke refresh tokens
    if (token) {
      try {
        await axios.post('/api/auth/logout', {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      } catch {
        // Ignore errors, still clear local data
      }
    }
    clearAuthData()
  }, [token, clearAuthData])

  // Login with an existing token (for OAuth callback)
  // Note: OAuth callback should return refresh_token in the response
  const loginWithToken = useCallback(async (newToken: string, newRefreshToken?: string) => {
    // Validate the token by calling the validate endpoint
    const response = await axios.get<ApiResponse<{ user: UserAuthInfo; tenants: TenantInfo[] }>>('/api/auth/me', {
      headers: {
        Authorization: `Bearer ${newToken}`,
      },
    })

    // Extract data from the API response wrapper
    const meData = response.data.data || response.data
    const { user: newUser, tenants: newTenants } = meData as { user: UserAuthInfo; tenants: TenantInfo[] }

    // Store auth data - create a LoginResponse-like object
    // Note: For OAuth, the refresh token should be passed from the callback
    const loginData: LoginResponse = {
      token: newToken,
      refresh_token: newRefreshToken || '',
      expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(), // Default 15 min if not provided
      user: newUser,
      tenants: newTenants,
    }
    storeAuthData(loginData)
  }, [storeAuthData])

  const switchTenant = useCallback(async (tenantId: string) => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await axios.post<ApiResponse<LoginResponse>>('/api/auth/switch-tenant', {
      tenant_id: tenantId,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    // Extract data from the API response wrapper
    const loginData = (response.data.data || response.data) as LoginResponse
    storeAuthData(loginData)
  }, [token, storeAuthData])

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    await axios.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }, [token])

  const value: AuthContextType = {
    user,
    token,
    tenants,
    currentTenant,
    isAuthenticated: !!token && !!user,
    isLoading,
    isSuperAdmin: user?.is_super_admin ?? false,
    login,
    verifyOtp,
    loginWithToken,
    logout,
    switchTenant,
    changePassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Helper to get auth token for API calls
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

// Helper to check if user is authenticated
export function isAuthenticated(): boolean {
  return !!localStorage.getItem(TOKEN_KEY)
}

